/**
 * Public configuration usable from both server and client bundles.
 * Never put secrets here — secrets live in `lib/env.ts` (server-only).
 *
 * No tenant defaults ship here on purpose — every deployment must set these
 * in `.env` (see `.env.example`). An empty string fails loudly and early
 * rather than silently pointing at someone else's tenant.
 */

// `.env` templates ship the NEXT_PUBLIC_* overrides present-but-blank
// (`NEXT_PUBLIC_SITE_URL=`). That is an empty STRING, not undefined, so a
// plain `?? ` short-circuits on it and the server-side fallback
// (`SITE_URL`) is never reached — `absoluteUrl()` then produces relative
// URLs sitewide (canonical/JSON-LD/sitemap). Treat blank as "unset".
const firstNonEmpty = (...vals: Array<string | undefined>): string => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return '';
};

export const PUBLIC_API_BASE = firstNonEmpty(
  process.env.NEXT_PUBLIC_FLAMINGO_API_BASE,
  process.env.FLAMINGO_API_BASE,
);

export const PUBLIC_SITE_URL = firstNonEmpty(
  process.env.NEXT_PUBLIC_SITE_URL,
  process.env.SITE_URL,
);

export const PUBLIC_TENANT_HOST = firstNonEmpty(
  process.env.NEXT_PUBLIC_TENANT_HOST,
  process.env.TENANT_HOST,
);

export const PUBLIC_MAP_TILE_KEY = firstNonEmpty(
  process.env.NEXT_PUBLIC_MAP_TILE_KEY,
);

/**
 * Canonical public base for media files, e.g. `https://<site>/media`. When set,
 * every media URL the site emits (img/srcset, OG, JSON-LD, sitemaps, rich text)
 * is normalised onto it — see `toCanonicalMediaUrl` in lib/media. Must be a
 * NEXT_PUBLIC_ var so server and client render identical URLs (no hydration
 * mismatch). Unset = media URLs pass through as the API returns them.
 */
export const PUBLIC_MEDIA_BASE = firstNonEmpty(
  process.env.NEXT_PUBLIC_MEDIA_BASE,
).replace(/\/+$/, '');
